import React, {
    useMemo,
} from "react";

import {
    useSelector,
} from "react-redux";

import {
    FiBriefcase,
    FiCheckCircle,
    FiGlobe,
    FiKey,
    FiMail,
    FiMonitor,
    FiShield,
    FiShoppingBag,
    FiSmartphone,
    FiUser,
    FiUsers,
} from "react-icons/fi";

import {
    selectTenantUser,
    selectTenantRole,
    selectTenant,
    selectTenantPermissions,
    selectTenantProfileLoading,
} from "../../../../../../../store/tenantProfile/tenantProfileSlice";

import "./TenantProfile.css";


/*
|--------------------------------------------------------------------------
| DETAIL ITEM
|--------------------------------------------------------------------------
*/

const ProfileDetail = ({
    icon: Icon,
    label,
    value,
    children,
}) => {

    return (

        <div className="tenant-profile-detail">

            <div className="tenant-profile-detail-icon">

                {Icon ? (
                    <Icon />
                ) : null}

            </div>


            <div>

                <span>

                    {label}

                </span>


                {children || (

                    <strong>

                        {value || "-"}

                    </strong>

                )}

            </div>

        </div>

    );

};


/*
|--------------------------------------------------------------------------
| STATUS BADGE
|--------------------------------------------------------------------------
*/

const StatusBadge = ({
    value,
}) => {

    const status =
        String(
            value ||
            ""
        )
            .trim()
            .toUpperCase();


    return (

        <span
            className={`tenant-profile-status tenant-profile-status-${status.toLowerCase()}`}
        >

            {status || "-"}

        </span>

    );

};


/*
|--------------------------------------------------------------------------
| TENANT PROFILE
|--------------------------------------------------------------------------
*/

const TenantProfile = () => {

    /*
    |--------------------------------------------------------------------------
    | REDUX
    |--------------------------------------------------------------------------
    */

    const user =
        useSelector(
            selectTenantUser
        );


    const role =
        useSelector(
            selectTenantRole
        );


    const tenant =
        useSelector(
            selectTenant
        );


    const permissions =
        useSelector(
            selectTenantPermissions
        );


    const loading =
        useSelector(
            selectTenantProfileLoading
        );


    /*
    |--------------------------------------------------------------------------
    | OWNER
    |--------------------------------------------------------------------------
    */

    const isOwner =
        Boolean(
            user?.isOwner
        ) ||
        String(
            user?.userType ||
            ""
        )
            .trim()
            .toUpperCase() ===
            "OWNER";


    /*
    |--------------------------------------------------------------------------
    | PERMISSION GROUPS
    |--------------------------------------------------------------------------
    */

    const permissionGroups =
        useMemo(
            () => {

                const groups = {};


                (
                    permissions ||
                    []
                ).forEach(
                    (
                        permission
                    ) => {

                        const code =
                            String(
                                permission ||
                                ""
                            )
                                .trim()
                                .toLowerCase();


                        if (
                            !code
                        ) {

                            return;

                        }


                        const [
                            moduleName,
                        ] =
                            code.split(
                                "."
                            );


                        if (
                            !groups[
                                moduleName
                            ]
                        ) {

                            groups[
                                moduleName
                            ] =
                                [];

                        }


                        groups[
                            moduleName
                        ].push(
                            code
                        );

                    }
                );


                return groups;

            },
            [
                permissions,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    if (
        loading &&
        !user
    ) {

        return (

            <div className="tenant-profile-loading">

                <div className="tenant-profile-loader" />

                <strong>

                    Loading Profile

                </strong>

            </div>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | UI
    |--------------------------------------------------------------------------
    */

    return (

        <div className="tenant-profile-page">

            {/*
            |--------------------------------------------------------------------------
            | HEADER
            |--------------------------------------------------------------------------
            */}

            <section className="tenant-profile-header-card">

                <div className="tenant-profile-avatar">

                    {String(
                        user?.name ||
                        "U"
                    )
                        .charAt(
                            0
                        )
                        .toUpperCase()}

                </div>


                <div className="tenant-profile-header-main">

                    <div className="tenant-profile-name-row">

                        <h1>

                            {
                                user?.name ||
                                "User"
                            }

                        </h1>


                        {isOwner ? (

                            <span className="tenant-profile-owner-badge">

                                <FiShield />

                                Business Owner

                            </span>

                        ) : null}

                    </div>


                    <p>

                        {
                            role?.name ||
                            user?.role?.name ||
                            "Business User"
                        }

                    </p>


                    <div className="tenant-profile-header-meta">

                        <span>

                            <FiBriefcase />

                            {
                                tenant?.name ||
                                "Business"
                            }

                        </span>


                        <span>

                            <FiSmartphone />

                            {
                                user?.mobile ||
                                "-"
                            }

                        </span>


                        {user?.email ? (

                            <span>

                                <FiMail />

                                {
                                    user.email
                                }

                            </span>

                        ) : null}

                    </div>

                </div>

            </section>


            {/*
            |--------------------------------------------------------------------------
            | SUMMARY
            |--------------------------------------------------------------------------
            */}

            <section className="tenant-profile-summary-grid">

                <article>

                    <div className="tenant-profile-summary-icon">

                        <FiUser />

                    </div>


                    <div>

                        <span>

                            Account Type

                        </span>


                        <strong>

                            {
                                user?.userType ||
                                "-"
                            }

                        </strong>

                    </div>

                </article>


                <article>

                    <div className="tenant-profile-summary-icon">

                        <FiShield />

                    </div>


                    <div>

                        <span>

                            Role

                        </span>


                        <strong>

                            {
                                role?.name ||
                                user?.role?.name ||
                                "-"
                            }

                        </strong>

                    </div>

                </article>


                <article>

                    <div className="tenant-profile-summary-icon">

                        <FiKey />

                    </div>


                    <div>

                        <span>

                            Permissions

                        </span>


                        <strong>

                            {isOwner
                                ? "Full Access"
                                : permissions?.length ||
                                  0}

                        </strong>

                    </div>

                </article>


                <article>

                    <div className="tenant-profile-summary-icon">

                        <FiCheckCircle />

                    </div>


                    <div>

                        <span>

                            Account Status

                        </span>


                        <StatusBadge
                            value={
                                user?.status
                            }
                        />

                    </div>

                </article>

            </section>


            {/*
            |--------------------------------------------------------------------------
            | MAIN GRID
            |--------------------------------------------------------------------------
            */}

            <div className="tenant-profile-grid">

                {/*
                |--------------------------------------------------------------------------
                | USER INFORMATION
                |--------------------------------------------------------------------------
                */}

                <section className="tenant-profile-card">

                    <div className="tenant-profile-card-header">

                        <FiUser />


                        <div>

                            <h2>

                                Account Information

                            </h2>


                            <p>

                                Your current authenticated business account.

                            </p>

                        </div>

                    </div>


                    <div className="tenant-profile-detail-grid">

                        <ProfileDetail
                            icon={
                                FiUser
                            }
                            label="Name"
                            value={
                                user?.name
                            }
                        />


                        <ProfileDetail
                            icon={
                                FiSmartphone
                            }
                            label="Mobile"
                            value={
                                user?.mobile
                            }
                        />


                        <ProfileDetail
                            icon={
                                FiMail
                            }
                            label="Email"
                            value={
                                user?.email
                            }
                        />


                        <ProfileDetail
                            icon={
                                FiUsers
                            }
                            label="User Type"
                            value={
                                user?.userType
                            }
                        />


                        <ProfileDetail
                            icon={
                                FiShield
                            }
                            label="Role Code"
                            value={
                                role?.code ||
                                user?.role?.code
                            }
                        />


                        <ProfileDetail
                            icon={
                                FiCheckCircle
                            }
                            label="Status"
                        >

                            <StatusBadge
                                value={
                                    user?.status
                                }
                            />

                        </ProfileDetail>

                    </div>

                </section>


                {/*
                |--------------------------------------------------------------------------
                | BUSINESS INFORMATION
                |--------------------------------------------------------------------------
                */}

                <section className="tenant-profile-card">

                    <div className="tenant-profile-card-header">

                        <FiBriefcase />


                        <div>

                            <h2>

                                Business Information

                            </h2>


                            <p>

                                Current tenant associated with your session.

                            </p>

                        </div>

                    </div>


                    <div className="tenant-profile-detail-grid">

                        <ProfileDetail
                            icon={
                                FiBriefcase
                            }
                            label="Business Name"
                            value={
                                tenant?.name
                            }
                        />


                        <ProfileDetail
                            icon={
                                FiKey
                            }
                            label="Business Code"
                            value={
                                tenant?.code
                            }
                        />


                        <ProfileDetail
                            icon={
                                FiGlobe
                            }
                            label="Subdomain"
                            value={
                                tenant?.subdomain
                            }
                        />


                        <ProfileDetail
                            icon={
                                FiGlobe
                            }
                            label="Custom Domain"
                            value={
                                tenant?.customDomain
                            }
                        />


                        <ProfileDetail
                            icon={
                                FiCheckCircle
                            }
                            label="Business Status"
                        >

                            <StatusBadge
                                value={
                                    tenant?.status
                                }
                            />

                        </ProfileDetail>


                        <ProfileDetail
                            icon={
                                FiShoppingBag
                            }
                            label="Subscription"
                        >

                            <StatusBadge
                                value={
                                    tenant
                                        ?.subscriptionStatus
                                }
                            />

                        </ProfileDetail>

                    </div>

                </section>


                {/*
                |--------------------------------------------------------------------------
                | FEATURES
                |--------------------------------------------------------------------------
                */}

                <section className="tenant-profile-card">

                    <div className="tenant-profile-card-header">

                        <FiMonitor />


                        <div>

                            <h2>

                                Enabled Features

                            </h2>


                            <p>

                                Platform capabilities currently enabled for your business.

                            </p>

                        </div>

                    </div>


                    <div className="tenant-profile-feature-grid">

                        <article
                            className={
                                tenant?.storefrontEnabled
                                    ? "enabled"
                                    : "disabled"
                            }
                        >

                            <FiMonitor />


                            <div>

                                <strong>

                                    Storefront

                                </strong>


                                <span>

                                    {tenant?.storefrontEnabled
                                        ? "Enabled"
                                        : "Disabled"}

                                </span>

                            </div>

                        </article>


                        <article
                            className={
                                tenant?.marketplaceEnabled
                                    ? "enabled"
                                    : "disabled"
                            }
                        >

                            <FiShoppingBag />


                            <div>

                                <strong>

                                    Marketplace

                                </strong>


                                <span>

                                    {tenant?.marketplaceEnabled
                                        ? "Enabled"
                                        : "Disabled"}

                                </span>

                            </div>

                        </article>

                    </div>

                </section>


                {/*
                |--------------------------------------------------------------------------
                | PERMISSIONS
                |--------------------------------------------------------------------------
                */}

                <section className="tenant-profile-card tenant-profile-permissions-card">

                    <div className="tenant-profile-card-header">

                        <FiKey />


                        <div>

                            <h2>

                                Access Permissions

                            </h2>


                            <p>

                                Current permission codes granted through your tenant role.

                            </p>

                        </div>

                    </div>


                    {isOwner ? (

                        <div className="tenant-profile-full-access">

                            <FiShield />


                            <div>

                                <strong>

                                    Full Tenant Access

                                </strong>


                                <span>

                                    Business Owner accounts bypass normal role permission restrictions.

                                </span>

                            </div>

                        </div>

                    ) : Object.keys(
                        permissionGroups
                    ).length >
                      0 ? (

                        <div className="tenant-profile-permission-groups">

                            {Object.entries(
                                permissionGroups
                            ).map(
                                ([
                                    moduleName,
                                    modulePermissions,
                                ]) => (

                                    <div
                                        className="tenant-profile-permission-group"
                                        key={
                                            moduleName
                                        }
                                    >

                                        <h3>

                                            {
                                                moduleName
                                                    .replace(
                                                        /_/g,
                                                        " "
                                                    )
                                            }

                                        </h3>


                                        <div className="tenant-profile-permission-list">

                                            {modulePermissions.map(
                                                (
                                                    permission
                                                ) => (

                                                    <span
                                                        key={
                                                            permission
                                                        }
                                                    >

                                                        <FiCheckCircle />

                                                        {
                                                            permission
                                                        }

                                                    </span>

                                                )
                                            )}

                                        </div>

                                    </div>

                                )
                            )}

                        </div>

                    ) : (

                        <div className="tenant-profile-no-permissions">

                            <FiKey />


                            <strong>

                                No Permissions Assigned

                            </strong>

                        </div>

                    )}

                </section>

            </div>

        </div>

    );

};


export default TenantProfile;